import { createContext, useContext, useState, useCallback, useMemo } from 'react'
interface SidebarContextType {
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  sidebarCollapsed: boolean
  setSidebarCollapsed: (collapsed: boolean) => void
}

interface SidebarProviderProps {
  children: React.ReactNode
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export function SidebarProvider({ children }: SidebarProviderProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  
  // Initialize sidebar state with localStorage persistence
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    if (typeof window !== 'undefined') {
      // Try to get saved state from localStorage first
      const savedState = localStorage.getItem('sidebar-collapsed')
      if (savedState !== null) {
        const collapsed = JSON.parse(savedState)
        return collapsed
      }
      
      // Fallback to screen size detection
      const initialCollapsed = window.innerWidth >= 1024
      return initialCollapsed
    }
    return false
  })

  // Wrapper function to persist state to localStorage
  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    localStorage.setItem('sidebar-collapsed', JSON.stringify(collapsed))
    setSidebarCollapsedState(collapsed)
  }, [])

  const value = useMemo<SidebarContextType>(() => ({
    sidebarOpen,
    setSidebarOpen,
    sidebarCollapsed,
    setSidebarCollapsed,
  }), [sidebarOpen, sidebarCollapsed, setSidebarCollapsed])

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  )
} 